import {Fragment, useState, type CSSProperties, type ReactNode} from 'react';
import {isAxiosError} from 'axios';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  RefreshCw,
  RotateCcw,
} from 'lucide-react';
import {
  isManagerBriefNotEnoughData,
  useGenerateManagerRecommendation,
  useManagerBrief,
} from '@api/meerec/hooks';
import type {
  ManagerBrief,
  ManagerBriefMetric,
  ManagerBriefMetricStatus,
  ManagerBriefMetrics,
  ManagerRecommendationPart,
} from '@api/meerec/types';
import {usePersonalScope} from '@api/workspaces';
import {cn} from '@lib/utils';
import {SparkleAi} from '@ui/primitives/SparkleAi';
import {Button} from '@ui/shadcn/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@ui/shadcn/collapsible';
import {Progress} from '@ui/shadcn/progress';
import {Skeleton} from '@ui/shadcn/skeleton';
import {Spinner} from '@ui/shadcn/spinner';
import {splitRecommendationCalls} from '../model/recommendation-calls';

const NOT_ENOUGH_DATA =
  'За последние 30 дней мало целевых звонков — анализ появится, когда их станет больше.';

const DAY_MS = 86_400_000;
const dayFormat = new Intl.DateTimeFormat('ru-RU', {
  day: 'numeric',
  month: 'short',
  timeZone: 'UTC',
});

/** «за 9–16 окт.» — скользящее окно `periodDays` суток, заканчивается в `asOfDate` (сегодня входит). */
const periodLabel = ({
  asOfDate,
  periodDays,
}: {
  asOfDate: string;
  periodDays: number;
}): string => {
  const end = Date.parse(`${asOfDate}T00:00:00Z`);
  if (Number.isNaN(end)) return `за ${periodDays} дней`;
  const from = new Date(end - periodDays * DAY_MS);
  const to = new Date(end);
  return from.getUTCMonth() === to.getUTCMonth()
    ? `за ${from.getUTCDate()}–${dayFormat.format(to)}`
    : `за ${dayFormat.format(from)} – ${dayFormat.format(to)}`;
};

/** «по 42 целевым звонкам» / «по вашему 21 целевому звонку». */
const callsLabel = (count: number, self: boolean): string => {
  const singular = count % 10 === 1 && count % 100 !== 11;
  return `по ${self ? (singular ? 'вашему ' : 'вашим ') : ''}${count} ${singular ? 'целевому звонку' : 'целевым звонкам'}`;
};

/** «завтра с 06:00» — когда разблокируется «Обновить анализ». */
const unlockLabel = (iso: string): string => {
  const at = new Date(iso);
  const time = at.toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
  });
  const days = Math.round(
    (new Date(at).setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0)) /
      DAY_MS,
  );
  const day =
    days <= 0
      ? 'сегодня'
      : days === 1
        ? 'завтра'
        : at.toLocaleDateString('ru-RU', {day: 'numeric', month: 'long'});
  return `${day} с ${time}`;
};

// ─── Карточка «Нейроаналитик» (анализ LLM) ──────────────────────────────────

type CardTone = 'accent' | 'muted' | 'dashed';

const CARD_TONE_CLASSES: Record<CardTone, string> = {
  accent: 'bg-[var(--color-primary-soft)]',
  muted: 'border border-[var(--color-border)] bg-[var(--color-background)]',
  dashed:
    'border border-dashed border-[var(--color-border)] bg-[var(--color-background)]',
};

/** Карточка «Нейроаналитик»: звёздочка слева, подпись и содержимое справа. */
const BriefCard = ({
  tone,
  meta,
  children,
  sparkleClassName,
  role,
}: {
  tone: CardTone;
  meta?: ReactNode;
  children: ReactNode;
  sparkleClassName?: string;
  role?: 'status' | 'alert';
}) => (
  <div
    role={role}
    aria-live={role === 'status' ? 'polite' : undefined}
    className={cn(
      'flex items-start gap-2.5 rounded-lg px-3.5 py-3',
      CARD_TONE_CLASSES[tone],
    )}
  >
    <SparkleAi
      size={16}
      tone='purple'
      className={cn('mt-px shrink-0', sparkleClassName)}
    />
    <div className='flex min-w-0 flex-1 flex-col gap-1'>
      <div className='text-[11px] text-[var(--color-muted-foreground)]'>
        <span className='font-semibold text-[var(--color-foreground)]'>
          Нейроаналитик
        </span>
        {meta}
      </div>
      {children}
    </div>
  </div>
);

const MutedText = ({children}: {children: ReactNode}) => (
  <p className='text-xs leading-normal text-[var(--color-muted-foreground)]'>
    {children}
  </p>
);

const ErrorCard = ({onRetry}: {onRetry: () => void}) => (
  <BriefCard tone='muted' role='alert' sparkleClassName='opacity-55'>
    <MutedText>
      Не удалось получить анализ. Остальные данные профиля доступны.
    </MutedText>
    <div className='mt-1.5'>
      <Button variant='outline' size='sm' onClick={onRetry}>
        <RotateCcw size={12} />
        Повторить
      </Button>
    </div>
  </BriefCard>
);

const NotEnoughDataCard = () => (
  <BriefCard tone='dashed' sparkleClassName='opacity-55'>
    <MutedText>{NOT_ENOUGH_DATA}</MutedText>
  </BriefCard>
);

type Generate = ReturnType<typeof useGenerateManagerRecommendation>;

type OpenCall = (communicationSid: string) => void;

/** Части анализа отдельными плашками: сначала что получается, затем что исправить; пустые не показываем. */
const RECOMMENDATION_PARTS = [
  {
    key: 'strength',
    label: 'Что получается',
    Icon: CheckCircle2,
    tileClassName:
      'border-[var(--score-good)]/40 bg-[var(--score-good-soft)] text-[var(--score-good)]',
  },
  {
    key: 'problem',
    label: 'Что исправить',
    Icon: AlertTriangle,
    tileClassName:
      'border-[var(--score-bad)]/40 bg-[var(--score-bad-soft)] text-[var(--score-bad)]',
  },
] as const;

/** Текст части; подписи звонков («19.10 06:44») открывают звонок поверх карточки. */
const RecommendationText = ({
  title,
  text,
  calls,
  onOpenCall,
}: ManagerRecommendationPart & {onOpenCall?: OpenCall}) => (
  <p className='text-[13px] leading-normal text-[var(--color-foreground)]'>
    <strong className='font-semibold'>{title}.</strong>{' '}
    {splitRecommendationCalls(text, onOpenCall ? calls : []).map(
      ({text: fragment, communicationSid}, index) =>
        communicationSid && onOpenCall ? (
          <button
            key={index}
            type='button'
            className='font-medium text-[var(--color-primary)] underline-offset-2 hover:underline'
            onClick={() => onOpenCall(communicationSid)}
          >
            {fragment}
          </button>
        ) : (
          <Fragment key={index}>{fragment}</Fragment>
        ),
    )}
  </p>
);

/** Полученный анализ: показывается, пока его не обновят (раз в сутки). */
const RecommendationCard = ({
  brief,
  generate,
  onOpenCall,
}: {
  brief: ManagerBrief;
  generate: Generate;
  onOpenCall?: OpenCall;
}) => {
  const recommendation = brief.recommendation;
  if (!recommendation) return null;
  // У анализов до v4 частей нет — показываем единственную тему.
  const parts = RECOMMENDATION_PARTS.flatMap((item) => {
    const part = recommendation[item.key];
    return part ? [{...item, part}] : [];
  });
  return (
    <BriefCard
      tone='accent'
      meta={
        <>
          <span aria-hidden='true'> · </span>
          {callsLabel(
            recommendation.targetedCalls,
            recommendation.audience === 'manager-self',
          )}{' '}
          {periodLabel(recommendation)}
        </>
      }
    >
      <div
        className={cn(
          'flex flex-col gap-2 transition-opacity',
          generate.isPending && 'opacity-60',
        )}
      >
        {parts.length > 0 ? (
          parts.map(({key, label, Icon, tileClassName, part}) => (
            <section
              key={key}
              aria-label={label}
              className={cn(
                'flex flex-col gap-1 rounded-md border px-3 py-2',
                tileClassName,
              )}
            >
              <span className='inline-flex items-center gap-1 text-[11px] font-semibold'>
                <Icon size={12} aria-hidden='true' />
                {label}
              </span>
              <RecommendationText {...part} onOpenCall={onOpenCall} />
            </section>
          ))
        ) : (
          <RecommendationText
            title={recommendation.title}
            text={recommendation.text}
          />
        )}
      </div>
      <div className='mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs'>
        {generate.isPending ? (
          <span
            role='status'
            className='inline-flex items-center gap-1.5 text-[var(--color-muted-foreground)]'
          >
            <Spinner className='size-3' aria-hidden='true' />
            Обновляем анализ…
          </span>
        ) : brief.canRefresh ? (
          <Button
            variant='link'
            size='sm'
            className='h-auto gap-1 p-0 text-xs font-semibold'
            onClick={() => generate.mutate()}
          >
            <RefreshCw size={12} />
            Обновить анализ
          </Button>
        ) : (
          brief.nextRefreshAt && (
            <span className='text-[var(--color-muted-foreground)]'>
              Обновить анализ можно {unlockLabel(brief.nextRefreshAt)}
            </span>
          )
        )}
        {generate.isError && (
          <span className='text-[var(--color-muted-foreground)]'>
            {isManagerBriefNotEnoughData(generate.error)
              ? 'Обновить не удалось: за последние 30 дней мало целевых звонков.'
              : 'Не удалось обновить анализ, попробуйте ещё раз.'}
          </span>
        )}
      </div>
    </BriefCard>
  );
};

/** Анализ LLM: получить по кнопке, показать полученный, состояния генерации. */
const NeuroCard = ({
  brief,
  generate,
  self,
  onOpenCall,
}: {
  brief: ManagerBrief;
  generate: Generate;
  self: boolean;
  onOpenCall?: OpenCall;
}) => {
  if (brief.recommendation)
    return (
      <RecommendationCard
        brief={brief}
        generate={generate}
        onOpenCall={onOpenCall}
      />
    );
  if (generate.isPending)
    return (
      <BriefCard
        tone='accent'
        role='status'
        meta=' разбирает звонки…'
        sparkleClassName='animate-pulse'
      >
        <Skeleton className='mt-1 h-2.5 w-full bg-[var(--color-primary)]/15' />
        <Skeleton className='h-2.5 w-[68%] bg-[var(--color-primary)]/15' />
      </BriefCard>
    );
  // Без показателей за 30 дней анализу не на чем строиться — кнопку не даём.
  if (
    !brief.metrics ||
    (generate.isError && isManagerBriefNotEnoughData(generate.error))
  )
    return <NotEnoughDataCard />;
  if (generate.isError) return <ErrorCard onRetry={() => generate.mutate()} />;
  return (
    <BriefCard tone='accent'>
      <MutedText>
        {self
          ? 'Сопоставит ваши показатели с выводами по звонкам и подскажет, над чем поработать.'
          : 'Сопоставит показатели менеджера с выводами по звонкам и подскажет, на чём сфокусироваться.'}
      </MutedText>
      <div className='mt-1.5'>
        <Button
          size='sm'
          disabled={!brief.canRefresh}
          onClick={() => generate.mutate()}
        >
          Получить анализ
        </Button>
      </div>
    </BriefCard>
  );
};

// ─── Показатели (без LLM) ───────────────────────────────────────────────────

type Tone = 'bad' | 'default' | 'good';

const STATUS_TONE: Record<ManagerBriefMetricStatus, Tone> = {
  down: 'bad',
  normal: 'default',
  up: 'good',
};

/** Тон балла 0..5 — по порогам статуса метрики на бэке (2.5 / 4.0). */
const scoreTone = (score: number): Tone =>
  score < 2.5 ? 'bad' : score < 4 ? 'default' : 'good';

const TONE_TEXT: Record<Tone, string> = {
  bad: 'text-[var(--score-bad)]',
  default: 'text-[var(--color-foreground)]',
  good: 'text-[var(--score-good)]',
};

/** Границы зон шкалы в процентах: 2.5 и 4.0 из 5. */
const ZONE_TICKS = [50, 80];

/** Сколько худших метрик видно сразу (или все `down`, если их больше). */
const VISIBLE_METRICS = 3;

/**
 * Строка: подпись | шкала | менеджер | команда. На мобильном шкала уходит
 * под подпись на всю ширину.
 */
const METRIC_GRID =
  'grid grid-cols-[minmax(0,1fr)_3.5rem_3.5rem] items-center gap-x-3 md:grid-cols-[13rem_minmax(0,1fr)_3.5rem_3.5rem]';

/**
 * Общая шкала 0..1 поверх `Progress`: цвет — тон, зазоры — границы зон
 * «ниже нормы / норма / сильно».
 */
const ScaleBar = ({
  ratio,
  tone,
  label,
}: {
  ratio: number;
  tone: Tone;
  label: string;
}) => (
  <div className='relative'>
    <Progress
      aria-label={label}
      value={Math.round(Math.min(Math.max(ratio, 0), 1) * 100)}
      className='h-1.5'
      style={{'--color-primary': `var(--score-${tone})`} as CSSProperties}
    />
    {ZONE_TICKS.map((left) => (
      <span
        key={left}
        aria-hidden='true'
        className='absolute inset-y-0 w-0.5 -translate-x-1/2 bg-[var(--color-surface)]'
        style={{left: `${left}%`}}
      />
    ))}
  </div>
);

const MetricRow = ({metric}: {metric: ManagerBriefMetric}) => {
  const tone = STATUS_TONE[metric.status];
  return (
    <li className={cn(METRIC_GRID, 'gap-y-1.5')}>
      <div className='min-w-0 md:order-1'>
        <p className='truncate text-xs font-medium'>{metric.label}</p>
        <p className='truncate text-[10px] text-[var(--color-muted-foreground)]'>
          {metric.trendHint}
        </p>
      </div>
      <span
        className={cn(
          'text-right text-xs font-semibold tabular-nums md:order-3',
          TONE_TEXT[tone],
        )}
      >
        {metric.displayValue}
      </span>
      <span className='text-right text-xs tabular-nums text-[var(--color-muted-foreground)] md:order-4'>
        {metric.teamAverage ?? '—'}
      </span>
      <div className='col-span-3 md:order-2 md:col-span-1'>
        <ScaleBar
          ratio={metric.ratio}
          tone={tone}
          label={`${metric.label}: ${metric.displayValue}`}
        />
      </div>
    </li>
  );
};

/** Показатели за окно анализа, худшие первыми; со средним по команде. */
const MetricsBlock = ({metrics}: {metrics: ManagerBriefMetrics}) => {
  const [open, setOpen] = useState(false);
  const downCount = metrics.items.filter((m) => m.status === 'down').length;
  const visible = Math.max(VISIBLE_METRICS, downCount);
  // Прятать одну метрику за кнопкой бессмысленно — показываем сразу все.
  const headCount =
    metrics.items.length <= visible + 1 ? metrics.items.length : visible;
  const rest = metrics.items.slice(headCount);
  const {overall} = metrics;

  return (
    <section className='rounded-lg border border-[var(--color-border)] p-3.5'>
      <header className='mb-3 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1'>
        <h3 className='text-xs font-semibold'>
          Показатели {periodLabel(metrics)}
          <span className='font-normal text-[var(--color-muted-foreground)]'>
            {' · '}
            {callsLabel(metrics.targetedCalls, false)}
          </span>
        </h3>
        <p className='text-xs text-[var(--color-muted-foreground)]'>
          Оценка{' '}
          <span className='font-semibold tabular-nums text-[var(--color-foreground)]'>
            {(Math.round(overall.score * 10) / 10).toFixed(1)}
          </span>{' '}
          из {overall.maxScore}
          {' · '}
          <span
            className={cn('font-semibold', TONE_TEXT[scoreTone(overall.score)])}
          >
            {overall.grade}
          </span>
        </p>
      </header>
      <Collapsible open={open} onOpenChange={setOpen}>
        <div
          aria-hidden='true'
          className={cn(
            METRIC_GRID,
            'mb-2 gap-y-1 text-[10px] text-[var(--color-muted-foreground)]',
          )}
        >
          <span className='md:order-1' />
          <span className='text-right md:order-3'>Менеджер</span>
          <span className='text-right md:order-4'>Команда</span>
          <div className='col-span-3 flex md:order-2 md:col-span-1'>
            <span className='w-1/2'>ниже нормы</span>
            <span className='w-[30%] pl-1'>норма</span>
            <span className='flex-1 pl-1'>сильно</span>
          </div>
        </div>
        <ul className='space-y-2.5'>
          {metrics.items.slice(0, headCount).map((m) => (
            <MetricRow key={m.key} metric={m} />
          ))}
        </ul>
        {rest.length > 0 && (
          <>
            <CollapsibleContent>
              <ul className='mt-2.5 space-y-2.5'>
                {rest.map((m) => (
                  <MetricRow key={m.key} metric={m} />
                ))}
              </ul>
            </CollapsibleContent>
            <CollapsibleTrigger asChild>
              <Button
                variant='ghost'
                size='sm'
                className='mt-1.5 -ml-2 h-7 px-2 text-xs text-[var(--color-muted-foreground)]'
              >
                {open
                  ? 'Свернуть'
                  : `Показать все показатели (${metrics.items.length})`}
                <ChevronDown
                  size={12}
                  className={cn('transition-transform', open && 'rotate-180')}
                />
              </Button>
            </CollapsibleTrigger>
          </>
        )}
      </Collapsible>
    </section>
  );
};

/**
 * Блок «Нейроаналитик» в карточке менеджера (spec 20): анализ LLM по кнопке
 * (хранится у смотрящего, обновление раз в сутки) и показатели без LLM за
 * 7 дней (мало звонков — за 30) со средним по команде.
 */
/** `onOpenCall` — открыть звонок из текста анализа поверх карточки; без него даты — обычный текст. */
export const ManagerNeuroBrief = ({
  userSid,
  onOpenCall,
}: {
  userSid: string;
  onOpenCall?: OpenCall;
}) => {
  const query = useManagerBrief(userSid);
  const generate = useGenerateManagerRecommendation(userSid);
  const self = usePersonalScope().userSid === userSid;

  if (query.isPending)
    return (
      <div className='space-y-3'>
        <Skeleton className='h-[66px] w-full rounded-lg' />
        <Skeleton className='h-[160px] w-full rounded-lg' />
      </div>
    );
  if (query.isError)
    // 404 — менеджер вне scope: блок просто не показываем.
    return isAxiosError(query.error) &&
      query.error.response?.status === 404 ? null : (
      <ErrorCard onRetry={() => void query.refetch()} />
    );

  const brief = query.data;
  return (
    <div className='space-y-3'>
      <NeuroCard
        brief={brief}
        generate={generate}
        self={self}
        onOpenCall={onOpenCall}
      />
      {brief.metrics && <MetricsBlock metrics={brief.metrics} />}
    </div>
  );
};
